import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, MessageSquare, User, Bot, ChevronRight, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUserConversations = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    fetchUserInfo();
    fetchConversations();
  }, [userId]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`);
      setUserName(response.data.user.name || response.data.user.email);
    } catch (error) {
      console.error('獲取用戶信息失敗:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/conversations`);
      setConversations(response.data);
    } catch (error) {
      toast.error('獲取對話列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/conversations/${conversationId}`);
      setSelectedConversation(response.data);
    } catch (error) {
      toast.error('獲取對話詳情失敗');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-neutral-600">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-neutral-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(`/admin/users/${userId}`)}
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回用戶詳情
          </Button>
          <div>
            <h1 className="text-xl font-bold text-neutral-900">用戶對話記錄</h1>
            <p className="text-sm text-neutral-500">{userName}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-6">
          {/* Conversation List */}
          <div className="col-span-4">
            <Card className="p-4">
              <h3 className="font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                對話列表 ({conversations.length})
              </h3>
              
              {conversations.length === 0 ? (
                <div className="text-center text-neutral-500 py-12">
                  此用戶尚無對話記錄
                </div>
              ) : (
                <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv.id)}
                      className={`w-full text-left p-4 rounded-xl transition-all ${
                        selectedConversation?.id === conv.id
                          ? 'bg-primary-50 border-primary-200 border'
                          : 'bg-neutral-50 hover:bg-neutral-100 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-neutral-800 truncate">
                          {conv.agent_name || 'AI 助手'}
                        </span>
                        <ChevronRight className="w-4 h-4 text-neutral-400" />
                      </div>
                      <div className="flex items-center gap-2 text-xs text-neutral-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(conv.updated_at)}
                        <span className="ml-auto">{conv.messages?.length || 0} 則訊息</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Conversation Detail */}
          <div className="col-span-8">
            <Card className="p-6 min-h-[calc(100vh-150px)]">
              {selectedConversation ? (
                <>
                  <div className="border-b border-neutral-200 pb-4 mb-4">
                    <h3 className="font-semibold text-neutral-800">
                      {selectedConversation.agent_name || 'AI 助手'}
                    </h3>
                    <p className="text-sm text-neutral-500">
                      創建於 {formatDate(selectedConversation.created_at)}
                    </p>
                  </div>

                  <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
                    {selectedConversation.messages?.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          msg.role === 'user' ? 'bg-primary-100' : 'bg-neutral-100'
                        }`}>
                          {msg.role === 'user' ? (
                            <User className="w-4 h-4 text-primary-600" />
                          ) : (
                            <Bot className="w-4 h-4 text-neutral-600" />
                          )}
                        </div>
                        <div className={`flex-1 max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                          <div className={`inline-block px-4 py-3 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-primary-500 text-white rounded-br-md'
                              : 'bg-neutral-100 text-neutral-800 rounded-bl-md'
                          }`}>
                            {msg.role === 'assistant' ? (
                              <div className="prose prose-sm max-w-none">
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                              </div>
                            ) : (
                              <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                            )}
                          </div>
                          <div className="text-xs text-neutral-400 mt-1">
                            {msg.timestamp ? formatDate(msg.timestamp) : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-neutral-500">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                    <p>選擇一個對話查看詳情</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminUserConversations;
